import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, theme, Typography } from 'antd';
import { FormikErrors, FormikTouched } from 'formik';

import InfoIcon from 'assets/images/info.svg?react';
import { useSourceSlice } from 'entities/Source/hooks/useSourceSlice';
import { IArbitrageForm, IBloggerForm, IWebsiteForm } from 'entities/Source/model/types';
import { EOnboardingStates, EResultTypes, useOnboardingSlice } from 'features/Onboarding';
import { useMedia } from 'shared/hooks';
import { EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { ButtonStyleLink } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';

interface ICreateFormButtonsProps extends HTMLAttributes<HTMLDivElement> {
	isOnboarding?: boolean;
	isLoading: boolean;
	errors: FormikErrors<IWebsiteForm | IBloggerForm | IArbitrageForm>;
	touched: FormikTouched<IWebsiteForm | IBloggerForm | IArbitrageForm>;
	setSkipVerification: (skip: boolean) => void;
	isWebsite?: boolean;
}

export const CreateFormButtons: FC<ICreateFormButtonsProps> = ({
	isOnboarding,
	isLoading,
	errors,
	setSkipVerification,
	isWebsite,
}) => {
	const {
		token: { colorPrimary },
	} = theme.useToken();
	const { t } = useTranslation();
	const isMd = useMedia();
	const { setModalState, setResultType } = useOnboardingSlice();
	const { type } = useSourceSlice();

	return (
		<>
			<Flex justify="space-between" gap={16} vertical={isMd}>
				<Flex gap={8} vertical={isMd}>
					<Button
						type="primary"
						htmlType="submit"
						loading={isLoading}
						disabled={isLoading || !!Object.keys(errors).length}
						onClick={() =>
							sendYmEvent({
								event: EYmEvents.CLICK_VERIFY_ADSPACE,
								o1: [
									EYmEventsO1.ACCOUNT,
									EYmEventsO1.PUBLISHER,
									isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE,
								],
								o2: EYmEventsO2.ADD_ADSPACE,
								o4: type,
							})
						}>
						{t('Верифицировать площадку')}
					</Button>

					<Button
						variant="text"
						color="primary"
						htmlType="submit"
						onClick={() => {
							setSkipVerification(true);
							sendYmEvent({
								event: EYmEvents.CLICK_SLIP_VERIFY_ADSPACE,
								o1: [
									EYmEventsO1.ACCOUNT,
									EYmEventsO1.PUBLISHER,
									isOnboarding ? EYmEventsO1.WELCOME_POPUP : EYmEventsO1.ADSPACE,
								],
								o2: EYmEventsO2.ADD_ADSPACE,
								o4: type,
							});
						}}
						loading={isLoading}
						disabled={isLoading || !!Object.keys(errors).length}>
						{t('Пропустить верификацию')}
					</Button>
				</Flex>

				{!isOnboarding && (
					<ButtonStyleLink disabled={isLoading} to={`/${EMainRoutes.SOURCES}`}>
						{t('Отменить')}
					</ButtonStyleLink>
				)}

				{isOnboarding && (
					<Button
						variant="text"
						color="primary"
						disabled={isLoading}
						onClick={() => {
							setResultType(EResultTypes.NEED_SOURCE);
							setModalState(EOnboardingStates.FINAL);
							sendYmEvent({
								event: EYmEvents.CLICK_ADD_ADSPACE,
								o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.WELCOME_POPUP],
								o2: EYmEventsO2.ADD_ADSPACE,
							});
						}}>
						{t('Пропустить шаг')}
					</Button>
				)}
			</Flex>

			{!isWebsite && (
				<Flex gap={8}>
					<InfoIcon width={24} height={24} style={{ minWidth: 24, color: colorPrimary }} />
					<Flex vertical gap={4}>
						<Typography.Text strong>
							{t('Верификация необходима, чтобы вы смогли подавать заявки на оффера рекламодателей.')}
						</Typography.Text>

						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{t(
								'Для подтверждения владения площадкой с вами свяжется наша команда. Проверка обычно занимает от 2 часов до 3 дней, в зависимости от рабочего времени нашей команды. ',
							)}
						</Typography.Text>
					</Flex>
				</Flex>
			)}
		</>
	);
};
